import React, { createContext, useEffect, useState } from "react";

interface ViewportContextProps {
    width: number;
    height: number;
}

export const ViewportContext = createContext<ViewportContextProps>({
    width: window.innerWidth,
    height: window.innerHeight,
});

const ViewportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [viewport, setViewport] = useState<ViewportContextProps>({
        width: window.visualViewport?.width || window.innerWidth,
        height: window.visualViewport?.height || window.innerHeight,
    });

    useEffect(() => {
        let rafId: number | null = null;

        const updateViewport = () => {
            setViewport({
                width: window.visualViewport?.width || window.innerWidth,
                height: window.visualViewport?.height || window.innerHeight,
            });
        };

        const handleResize = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(updateViewport);
        };

        window.addEventListener("resize", handleResize);
        window.visualViewport?.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
            window.visualViewport?.removeEventListener("resize", handleResize);
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, []);

    return (
        <ViewportContext.Provider value={viewport}>
            {children}
        </ViewportContext.Provider>
    );
};

export const useViewport = () => {
    const context = React.useContext(ViewportContext);
    if (!context) {
        throw new Error("useViewport must be used within a ViewportProvider");
    }
    return context;
};

export default ViewportProvider;